import __request__ from 'routing/request';
interface RetentionStrategyConfig {
    readonly max_number_of_indices: number;
    readonly type: string;
}
interface IndexSetFieldRestriction {
    readonly type: string;
}
interface DataTieringStatus {
    readonly type: string;
}
interface IndexSetCreationRequest {
    readonly index_optimization_max_num_segments: number;
    readonly replicas: number;
    readonly field_type_refresh_interval: string;
    readonly description: string;
    readonly index_optimization_disabled: boolean;
    readonly creation_date: string;
    readonly field_type_profile: string;
    readonly title: string;
    readonly retention_strategy_class: string;
    readonly index_set_template_id: string;
    readonly retention_strategy: RetentionStrategyConfig;
    readonly writable: boolean;
    readonly shards: number;
    readonly index_analyzer: string;
    readonly rotation_strategy: RotationStrategyConfig;
    readonly field_restrictions: {
        readonly [_key: string]: IndexSetFieldRestriction[];
    };
    readonly index_template_type: string;
    readonly use_legacy_rotation: boolean;
    readonly rotation_strategy_class: string;
    readonly index_prefix: string;
    readonly data_tiering: DataTieringConfig;
}
interface DataTieringConfig {
    readonly index_lifetime_min: string;
    readonly index_lifetime_max: string;
    readonly type: string;
}
interface IndexSetUpdateRequest {
    readonly index_optimization_max_num_segments: number;
    readonly replicas: number;
    readonly field_type_refresh_interval: string;
    readonly description: string;
    readonly index_optimization_disabled: boolean;
    readonly field_type_profile: string;
    readonly title: string;
    readonly retention_strategy_class: string;
    readonly retention_strategy: RetentionStrategyConfig;
    readonly writable: boolean;
    readonly shards: number;
    readonly rotation_strategy: RotationStrategyConfig;
    readonly field_restrictions: {
        readonly [_key: string]: IndexSetFieldRestriction[];
    };
    readonly use_legacy_rotation: boolean;
    readonly id: string;
    readonly rotation_strategy_class: string;
    readonly data_tiering: DataTieringConfig;
}
interface RotationStrategyConfig {
    readonly type: string;
}
interface IndexSetStats {
    readonly indices: number;
    readonly size: number;
    readonly documents: number;
}
interface IndexSetResponse {
    readonly data_tiering_status: DataTieringStatus;
    readonly index_optimization_max_num_segments: number;
    readonly replicas: number;
    readonly field_type_refresh_interval: string;
    readonly description: string;
    readonly index_optimization_disabled: boolean;
    readonly can_be_default: boolean;
    readonly creation_date: string;
    readonly field_type_profile: string;
    readonly title: string;
    readonly retention_strategy_class: string;
    readonly retention_strategy: RetentionStrategyConfig;
    readonly writable: boolean;
    readonly shards: number;
    readonly default: boolean;
    readonly index_analyzer: string;
    readonly rotation_strategy: RotationStrategyConfig;
    readonly field_restrictions: {
        readonly [_key: string]: IndexSetFieldRestriction[];
    };
    readonly index_template_type: string;
    readonly use_legacy_rotation: boolean;
    readonly id: string;
    readonly rotation_strategy_class: string;
    readonly index_prefix: string;
    readonly data_tiering: DataTieringConfig;
}
interface IndexSetsResponse {
    readonly total: number;
    readonly stats: {
        readonly [_key: string]: IndexSetStats;
    };
    readonly index_sets: IndexSetResponse[];
}
/**
 * Get a list of all index sets
 * @param skip The number of elements to skip (offset).
 * @param limit The maximum number of elements to return.
 * @param stats Include index set stats.
 * @param only_open Include only graylog open indices.
 */
export function list(skip: number = 0, limit: number = 0, stats: boolean = false, only_open: boolean = false): Promise<IndexSetsResponse> {
    return __request__('GET', '/system/indices/index_sets', null, { 'skip': skip, 'limit': limit, 'stats': stats, 'only_open': only_open }, {
        'Accept': ['application/json']
    });
}
/**
 * Create index set
 */
export function save(Indexsetconfiguration: IndexSetCreationRequest): Promise<IndexSetResponse> {
    return __request__('POST', '/system/indices/index_sets', Indexsetconfiguration, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get a list of all index sets
 * @param searchTitle The number of elements to skip (offset).
 * @param skip The number of elements to skip (offset).
 * @param limit The maximum number of elements to return.
 * @param stats Include index set stats.
 */
export function search(skip: number = 0, limit: number = 0, searchTitle?: string, stats: boolean = false): Promise<IndexSetsResponse> {
    return __request__('GET', '/system/indices/index_sets/search', null, { 'searchTitle': searchTitle, 'skip': skip, 'limit': limit, 'stats': stats }, {
        'Accept': ['application/json']
    });
}
/**
 * Get stats of all index sets
 */
export function globalStats(): Promise<IndexSetStats> {
    return __request__('GET', '/system/indices/index_sets/stats', null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Update index set
 */
export function update(id: string, Indexsetconfiguration: IndexSetUpdateRequest): Promise<IndexSetResponse> {
    return __request__('PUT', `/system/indices/index_sets/${id}`, Indexsetconfiguration, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get index set
 */
export function get(id: string): Promise<IndexSetResponse> {
    return __request__('GET', `/system/indices/index_sets/${id}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete index set
 */
export function remove(id: string, delete_indices: boolean = true): Promise<void> {
    return __request__('DELETE', `/system/indices/index_sets/${id}`, null, { 'delete_indices': delete_indices }, {
        'Accept': ['application/json']
    });
}
/**
 * Set default index set
 */
export function setDefault(id: string): Promise<IndexSetResponse> {
    return __request__('PUT', `/system/indices/index_sets/${id}/default`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get index set statistics
 */
export function indexSetStatistics(id: string): Promise<IndexSetStats> {
    return __request__('GET', `/system/indices/index_sets/${id}/stats`, null, {}, {
        'Accept': ['application/json']
    });
}
